import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useUser } from '@/hooks/useUser'
import { organizationsService } from '@/services/organizations'
import { queryKeys } from '@/lib/query-keys'
import { ROUTES } from '@/constants/routes'
import type { OrganizationMember } from '@/types/user'
import { CUSTOMER_TYPE } from '@/constants/account'

const MEMBERS_PAGE_SIZE = 10

function getOrganizationId(user: {
  organization_quote?: { organization_id: string } | null
  subscriptions?: Array<{ organization_id?: string }>
}): string | null {
  return (
    user.organization_quote?.organization_id ?? user.subscriptions?.[0]?.organization_id ?? null
  )
}

function extractSeatCountFromName(productName: string): number {
  const match = productName.match(/Pack\s+(\d+)/i)
  return match ? parseInt(match[1], 10) : 0
}

function getTotalSeatsFromSubscription(
  subscription: {
    product?: { name?: string; metadata?: Record<string, unknown> }
  } | null
): number {
  if (!subscription?.product) return 0
  try {
    const meta = subscription.product.metadata
    if (meta && typeof meta === 'object') {
      const raw =
        meta.licences !== undefined && meta.licences !== null ? String(meta.licences).trim() : ''
      if (raw !== '') {
        const n = parseInt(raw, 10)
        if (Number.isFinite(n) && n >= 0) return n
      }
    }
  } catch {
    /* ignore invalid metadata */
  }
  return extractSeatCountFromName(subscription.product.name ?? '')
}

function memberDisplayName(m: OrganizationMember): string {
  const u = m.user
  const first = u.first_name?.trim() ?? ''
  const last = u.last_name?.trim() ?? ''
  if (last && first) return `${first} ${last}`
  if (last) return last
  if (first) return first
  return u.email ?? ''
}

export function SeatsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: user, isLoading, isError, error } = useUser()
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)
  const [page, setPage] = useState(0)

  const organizationId = user ? getOrganizationId(user) : null
  const isOrganization = user?.customer_type === CUSTOMER_TYPE.ORGANIZATION

  const listParams = { limit: MEMBERS_PAGE_SIZE, offset: page * MEMBERS_PAGE_SIZE }
  const { data: membersData, isLoading: membersLoading } = useQuery({
    queryKey: queryKeys.organizationMembers.list(organizationId!, listParams),
    queryFn: () => organizationsService.getMembers(organizationId!, listParams),
    enabled: !!organizationId,
  })

  const members = membersData?.results ?? []
  const totalCount = membersData?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / MEMBERS_PAGE_SIZE))
  const hasNext = Boolean(membersData?.next)
  const hasPrevious = Boolean(membersData?.previous)

  const licenseActivated = user?.license_activated !== false
  const totalSeats = getTotalSeatsFromSubscription(
    user?.subscriptions?.[0] ?? user?.pending_payment ?? null
  )
  const activeSeats = user?.total_users ?? 0
  const availableSeats = Math.max(0, totalSeats - activeSeats)

  const refetchMembers = () => {
    if (organizationId) {
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', organizationId] })
    }
  }

  const handleRemoveMember = async (member: OrganizationMember) => {
    if (!organizationId) return
    const name = memberDisplayName(member)
    if (!window.confirm(t('seats.errors.removeConfirm', { name }))) return
    setActionError(null)
    setActionLoadingId(member.user.id)
    try {
      await organizationsService.removeMember(organizationId, member.user.id)
      toast.success(t('seats.removeSuccess'))
      refetchMembers()
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.me })
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined
      if (status === 404) {
        refetchMembers()
        await queryClient.invalidateQueries({ queryKey: queryKeys.users.me })
      } else {
        setActionError(t('seats.errors.removeFailed'))
      }
    } finally {
      setActionLoadingId(null)
    }
  }

  if (isLoading) {
    return (
      <div className="bg-background px-4 py-8">
        <div className="mx-auto max-w-270">
          <div className="bg-placeholder mb-6 h-7 w-48 rounded" />
          <div className="bg-placeholder h-4 w-64 rounded" />
          <div className="border-border bg-card mt-6 animate-pulse rounded-2xl border p-6">
            <div className="bg-placeholder mb-4 h-4 w-full rounded" />
            <div className="bg-placeholder mb-4 h-4 w-full rounded" />
            <div className="bg-placeholder h-4 w-full rounded" />
          </div>
        </div>
      </div>
    )
  }

  if (isError || !user) {
    return (
      <div className="bg-background px-4 py-8">
        <div className="mx-auto max-w-270">
          <h1 className="text-heading mb-6 text-xl font-semibold">{t('seats.title')}</h1>
          <p className="text-destructive">
            {error instanceof Error ? error.message : t('profile.error')}
          </p>
        </div>
      </div>
    )
  }

  if (!isOrganization || !organizationId) {
    navigate(ROUTES.DASHBOARD, { replace: true })
    return null
  }

  return (
    <div className="bg-background px-4 py-8">
      <div className="mx-auto max-w-270">
        <h1 className="text-heading mb-6 text-xl font-semibold">{t('seats.title')}</h1>

        {actionError && (
          <div
            className="border-destructive bg-destructive-light text-destructive mb-4 rounded-lg border p-4 text-sm"
            role="alert"
          >
            {actionError}
          </div>
        )}

        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <p className="text-foreground border-seats-stat-border rounded-xl border px-3 py-2 text-sm">
              {t('seats.availableSeats')} :{' '}
              <span className="text-seats-available text-base font-bold">{availableSeats}</span>
            </p>
            <p className="text-foreground border-seats-stat-border rounded-xl border px-3 py-2 text-sm">
              {t('seats.activeSeats')} :{' '}
              <span className="text-success text-base font-bold">{activeSeats}</span>
            </p>
          </div>
          <Link
            to={ROUTES.SEATS_ADD}
            className={`rounded-lg px-4 py-2.5 text-sm font-medium transition-opacity ${
              licenseActivated
                ? 'bg-primary text-primary-foreground hover:opacity-90'
                : 'bg-muted text-muted-foreground cursor-not-allowed opacity-60'
            }`}
            aria-disabled={!licenseActivated}
            onClick={e => {
              if (!licenseActivated) e.preventDefault()
            }}
          >
            {t('seats.addMember')}
          </Link>
        </div>

        <div className="border-border bg-card overflow-hidden rounded-2xl border">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border bg-table-header border-b">
                  <th className="text-label px-6 py-3 text-sm font-medium">
                    {t('seats.columns.lastName')}
                  </th>
                  <th className="text-label px-6 py-3 text-sm font-medium">
                    {t('seats.columns.firstName')}
                  </th>
                  <th className="text-label px-6 py-3 text-sm font-medium">
                    {t('seats.columns.email')}
                  </th>
                  <th className="text-label px-6 py-3 text-sm font-medium">
                    {t('seats.columns.rpps')}
                  </th>
                  <th className="text-label px-6 py-3 text-sm font-medium">
                    {t('seats.columns.action')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {membersLoading ? (
                  <tr>
                    <td colSpan={5} className="text-foreground px-6 py-8 text-center">
                      {t('seats.loading')}
                    </td>
                  </tr>
                ) : members.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted-foreground px-6 py-8 text-center text-sm">
                      {t('seats.empty')}
                    </td>
                  </tr>
                ) : (
                  members.map(member => (
                    <tr
                      key={member.id}
                      className="border-border hover:bg-highlight border-b transition-colors last:border-b-0"
                    >
                      <td className="text-foreground px-6 py-4 font-medium">
                        {member.user.last_name ?? '—'}
                      </td>
                      <td className="text-foreground px-6 py-4">{member.user.first_name ?? '—'}</td>
                      <td className="text-foreground px-6 py-4">{member.user.email ?? '—'}</td>
                      <td className="text-foreground px-6 py-4">
                        {member.rpps_number ?? member.user.rpps_number ?? '—'}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          className="text-destructive cursor-pointer transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label={t('seats.actions.remove')}
                          onClick={() => handleRemoveMember(member)}
                          disabled={actionLoadingId !== null}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {totalCount > MEMBERS_PAGE_SIZE && (
          <div className="border-border bg-card mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl border px-4 py-3">
            <p className="text-muted-foreground text-sm">
              {t('seats.pagination.summary', {
                from: page * MEMBERS_PAGE_SIZE + 1,
                to: Math.min((page + 1) * MEMBERS_PAGE_SIZE, totalCount),
                total: totalCount,
              })}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={!hasPrevious || membersLoading}
                className="border-border text-foreground hover:bg-muted flex items-center gap-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={t('seats.pagination.previous')}
              >
                <ChevronLeft className="h-4 w-4" />
                {t('seats.pagination.previous')}
              </button>
              <span className="text-muted-foreground px-2 text-sm">
                {t('seats.pagination.page', { current: page + 1, total: totalPages })}
              </span>
              <button
                type="button"
                onClick={() => setPage(p => p + 1)}
                disabled={!hasNext || membersLoading}
                className="border-border text-foreground hover:bg-muted flex items-center gap-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={t('seats.pagination.next')}
              >
                {t('seats.pagination.next')}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
